import { FileField, TextField, useSubmitHandler } from '@ff-it/form';
import { useDialogController } from '@ff-it/ui';
import { DialogForm } from 'components';
import { ReactElement } from 'react';
import { toast } from 'react-toastify';
import type { KeyedMutator } from 'swr';
import type { EditValues, Integration } from './types';

type Props = {
  baseUrl: string;
  integration: Integration;
  mutate: KeyedMutator<Integration>;
};

export function EditDialog({ baseUrl, integration, mutate }: Props): ReactElement {
  const dialog = useDialogController();
  const close = (): void => dialog.setOpen(false);
  const configured = integration.state !== 'NOT_CONFIGURED';

  const onSubmit = useSubmitHandler<EditValues, Integration>(
    { url: baseUrl, method: 'PUT' },
    {
      mapValues: async (values) => ({
        project_id: values.project_id,
        dataset_id: values.dataset_id,
        ...(values.credentials ? { credentials: await values.credentials.text() } : {}),
      }),
      onSuccess: (next) => {
        mutate(next, { revalidate: false });
        toast.success('Integration has been saved.');
      },
    },
  );

  return (
    <DialogForm
      title="BigQuery configuration"
      initialValues={{
        project_id: integration.project_id || '',
        dataset_id: integration.dataset_id || '',
      }}
      submitHandler={onSubmit}
      onSubmit={close}
      onDismiss={close}
    >
      <div className="form-row">
        <TextField name="project_id" label="GCP project ID" className="col-md-6" required />
        <TextField
          name="dataset_id"
          label="Dataset ID"
          help="Just the dataset, e.g. niit_import - not project.dataset."
          className="col-md-6"
          required
        />
      </div>
      <div className="form-row">
        <FileField
          name="credentials"
          label="Service account JSON"
          className="col-12"
          help={
            configured
              ? `Currently ${integration.credentials_client_email ?? '(unknown)'}. Leave empty to keep it.`
              : 'Service account key file (.json) downloaded from GCP.'
          }
          required={!configured}
        />
      </div>
    </DialogForm>
  );
}
